import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, DIMENSION_META } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProgressRing from "@/components/ProgressRing";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { Sparkles, Target, TrendingUp, ArrowRight, Award } from "lucide-react";
import { toast } from "sonner";
import { generateCertificate } from "@/lib/certificate";

export default function DiagnosticResults() {
  const { user } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/diagnostic/latest").then((res) => setR(res.data)); }, []);

  if (!r) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <div className="font-serif text-3xl text-[#0B1E36] mb-3">No baseline yet.</div>
      <p className="text-slate-600 mb-8">Take the diagnostic to unlock your personalized growth plan.</p>
      <Link to="/app/diagnostic" data-testid="empty-take-diag" className="inline-block bg-[#EC4899] text-white px-6 py-3 rounded-full font-semibold">Take diagnostic</Link>
    </div>
  );

  const radarData = Object.entries(DIMENSION_META).map(([k, m]) => ({
    dim: m.label, value: r.scores[k] || 0, fullMark: 100,
  }));
  const maturity = Math.round(Object.values(r.scores).reduce((a, b) => a + b, 0) / 4);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10 fade-up">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Your Future Counts Index™ Profile</div>
        <h1 className="font-serif text-4xl text-[#0B1E36]">Growth plan</h1>
      </div>

      <div className="bg-[#0B1E36] text-white p-10 rounded-2xl mb-8 grid md:grid-cols-3 gap-8 items-center">
        <div className="md:col-span-1 flex justify-center">
          <ProgressRing value={maturity} size={190} color="#C9A961" label="FUTURE COUNTS INDEX" sublabel="of 100" testId="results-ring" />
        </div>
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 text-[#C9A961] text-xs uppercase tracking-widest mb-3">
            <Sparkles className="h-4 w-4" /> AI-generated read
          </div>
          <div className="font-serif text-2xl leading-snug" data-testid="ai-headline">"{r.summary.headline}"</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-8 rounded-2xl border border-slate-200">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Dimension map</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%" minHeight={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E2E8F0" />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "#0B1E36", fontSize: 12, fontWeight: 600 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <Radar name="You" dataKey="value" stroke="#EC4899" fill="#EC4899" fillOpacity={0.35} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white p-8 rounded-2xl border border-slate-200" data-testid="strengths-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-lg bg-[#2C7A7B]/15 grid place-items-center"><TrendingUp className="h-4 w-4 text-[#2C7A7B]" /></div>
              <div className="font-serif text-lg text-[#0B1E36]">Strengths</div>
            </div>
            <ul className="space-y-2 text-slate-700 text-sm leading-relaxed">
              {(r.summary.strengths || []).map((s, idx) => (
                <li key={idx} className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#2C7A7B]">{s}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-slate-200" data-testid="growth-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-lg bg-[#EC4899]/15 grid place-items-center"><Target className="h-4 w-4 text-[#EC4899]" /></div>
              <div className="font-serif text-lg text-[#0B1E36]">Growth areas</div>
            </div>
            <ul className="space-y-2 text-slate-700 text-sm leading-relaxed">
              {(r.summary.growth_areas || []).map((s, idx) => (
                <li key={idx} className="pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#EC4899]">{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white p-10 rounded-2xl border border-slate-200 mb-8" data-testid="priorities-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#C9A961] mb-1">Next 30 days</div>
            <div className="font-serif text-2xl text-[#0B1E36]">Your practice priorities</div>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {(r.summary.priorities || []).map((p, idx) => (
            <div key={idx} className="border-l-2 border-[#EC4899] pl-5 py-1">
              <div className="font-serif text-3xl text-[#EC4899]/50 mb-2">0{idx + 1}</div>
              <div className="text-slate-700 leading-relaxed text-sm">{p}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <Link to="/app/lessons" data-testid="results-to-lessons" className="bg-[#0B1E36] text-white px-6 py-3 rounded-full font-semibold btn-lift hover:bg-[#132a4a] inline-flex items-center gap-2">
          Start recommended lessons <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/app/scenarios" data-testid="results-to-scenarios" className="bg-white border border-slate-200 text-[#0B1E36] px-6 py-3 rounded-full font-semibold btn-lift hover:bg-slate-50">
          Try a scenario
        </Link>
        <button
          onClick={() => {
            const ok = generateCertificate({ user, result: r });
            if (!ok) toast.error("Popup blocked — allow popups to download your certificate.");
          }}
          data-testid="download-certificate"
          className="bg-[#C9A961] text-[#0B1E36] px-6 py-3 rounded-full font-semibold btn-lift hover:bg-[#B29455] inline-flex items-center gap-2 shadow-[0_8px_24px_rgba(201,169,97,0.3)]">
          <Award className="h-4 w-4" /> Download Future Counts Index™ Certificate
        </button>
      </div>
    </div>
  );
}
